<script setup>
import getUuid from 'widget/helpers/uuid';
import {
  computed,
  ref,
  onMounted,
  onUnmounted,
  defineEmits,
  defineExpose,
} from 'vue';
import { convertAudio } from './utils/mp3ConversionUtils';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
  // Aceleres: UI WhatsApp — true quando a gravação está pausada. Controla o
  // ícone Pausar↔Play e some com o ponto vermelho pulsante (visibility:hidden
  // pra não deslocar os outros elementos).
  isPaused: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'default',
  },
  playbackSrc: { type: String, default: '' },
  readOnly: { type: Boolean, default: false },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
  // Aceleres: disparado quando o microfone não está acessível (permissão
  // negada, contexto inseguro, WebView sem concessão etc.). Sem isso o
  // start() lançava no onMounted e o Vue abortava o re-render do composer
  // inteiro → "nada aparece" ao clicar no mic.
  'recordError',
  // Aceleres 2026-07-03: UI WhatsApp — botões da barra de gravação.
  // O ReplyBox trata cada um (delete/playpause/send).
  'delete',
  'playpause',
  'send',
]);

// ---- template refs / reactive state ----
const canvasRef = ref(null);
const timeText = ref('00:00');
// Aceleres: reprodução do trecho já gravado (botão ▶ no estado pausado).
const isPlayingBack = ref(false);
const isConversationLayout = computed(() => props.variant === 'conversation');

// ---- engine state (não-reativo) ----
let stream = null;
let audioCtx = null;
let analyser = null;
let source = null;
let mediaRecorder = null;
let chunks = [];
let rafId = null;
let timerId = null;
// Aceleres: <audio> usado só pra reproduzir o trecho gravado no estado pausado.
let playbackAudio = null;
let playbackRequest = null;

let isRecording = false;
// Aceleres: estado de pausa do MOTOR (renomeado p/ não colidir com a prop
// `isPaused` no template — vue/no-dupe-keys). A prop isPaused reflete o
// estado do ReplyBox e controla a UI; enginePaused controla o motor.
let enginePaused = false;
let aborted = false; // true quando o componente está sendo descartado (não emitir finish)

let startedAt = 0; // performance.now() no início da gravação
let accPausedMs = 0; // ms acumulados em pausa
let pauseStart = 0; // performance.now() do início da pausa atual

// suavização das barras (visualizador)
const BAR_COUNT = 44;
const smooth = new Array(BAR_COUNT).fill(0.04);
const MINIMUM_RECORDING_DURATION_MS = 2000;

// ---- helpers ----
const formatTime = ms => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const pickMime = () => {
  if (
    typeof MediaRecorder === 'undefined' ||
    typeof MediaRecorder.isTypeSupported !== 'function'
  ) {
    return '';
  }
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch (e) {
      /* ignore */
    }
  }
  return '';
};

const extFor = format => {
  if (/wav/i.test(format)) return 'wav';
  if (/ogg|opus/i.test(format)) return 'ogg';
  if (/mp4|m4a/i.test(format)) return 'm4a';
  if (/aac/i.test(format)) return 'aac';
  if (/mp3|mpeg/i.test(format)) return 'mp3';
  return 'webm';
};

const extForMime = mimeType => {
  if (/ogg/i.test(mimeType)) return 'ogg';
  if (/mp4|m4a/i.test(mimeType)) return 'm4a';
  if (/wav/i.test(mimeType)) return 'wav';
  return 'webm';
};

const requiresStrictConversion = format =>
  /audio\/(mp3|mpeg|wav)/i.test(format);

const supportsBrowserConversion = format =>
  /audio\/(mp3|mpeg|wav)/i.test(format);

// ---- timer ----
const tickTimer = () => {
  if (!isRecording) return;
  const now = performance.now();
  const elapsed = enginePaused
    ? pauseStart - startedAt - accPausedMs
    : now - startedAt - accPausedMs;
  const txt = formatTime(elapsed);
  timeText.value = txt;
  emit('recorderProgressChanged', txt);
};

const startTimer = () => {
  stopTimer();
  timerId = window.setInterval(tickTimer, 250);
};

const stopTimer = () => {
  if (timerId) {
    window.clearInterval(timerId);
    timerId = null;
  }
};

const recordedDurationMs = () => {
  if (!startedAt) return 0;

  const endedAt = enginePaused ? pauseStart : performance.now();
  return Math.max(0, endedAt - startedAt - accPausedMs);
};

// ---- visualizador (waveform cinza, fino, estilo WhatsApp Web) ----
const roundRectPath = (ctx, x, y, w, h, r) => {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
};

const draw = () => {
  if ((!isRecording || enginePaused) && !props.playbackSrc) return;
  const canvas = canvasRef.value;
  if (!canvas || (!analyser && !props.playbackSrc)) {
    rafId = requestAnimationFrame(draw);
    return;
  }
  const dpr = window.devicePixelRatio || 1;
  const cssW = canvas.clientWidth || 300;
  const cssH = canvas.clientHeight || 28;
  if (
    canvas.width !== Math.round(cssW * dpr) ||
    canvas.height !== Math.round(cssH * dpr)
  ) {
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
  }
  const ctx2d = canvas.getContext('2d');
  ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx2d.clearRect(0, 0, cssW, cssH);

  const data = analyser ? new Uint8Array(analyser.frequencyBinCount) : [];
  analyser?.getByteFrequencyData(data);

  const mid = cssH / 2;
  const maxBarH = cssH * 0.92;
  const slot = cssW / BAR_COUNT;
  const barW = Math.max(2, slot * 0.42); // barras finas
  const usableBins = Math.floor((analyser?.frequencyBinCount || 0) * 0.5);
  const binsPerBar = usableBins / BAR_COUNT;

  // Aceleres: waveform cinza WhatsApp (#9E9E9E).
  ctx2d.fillStyle = '#9E9E9E';

  for (let i = 0; i < BAR_COUNT; i++) {
    let sum = 0;
    let cnt = 0;
    const base = i * binsPerBar;
    for (let b = 0; b < binsPerBar; b++) {
      const v = data[Math.floor(base + b)];
      if (v !== undefined) {
        sum += v;
        cnt += 1;
      }
    }
    let amp = props.playbackSrc ? smooth[i] : cnt ? sum / cnt / 255 : 0; // 0..1
    amp = Math.max(amp, 0.05); // piso: silêncio ainda mostra barrinha
    // suavização (ataque rápido, decaimento lento)
    const prev = smooth[i];
    smooth[i] = amp > prev ? amp : prev * 0.82 + amp * 0.18;
    const barH = Math.max(2, smooth[i] * maxBarH);
    const x = i * slot + (slot - barW) / 2;
    roundRectPath(ctx2d, x, mid - barH / 2, barW, barH, barW / 2);
    ctx2d.fill();
  }

  if (!props.playbackSrc) rafId = requestAnimationFrame(draw);
};

const startVisualizer = () => {
  stopVisualizer();
  rafId = requestAnimationFrame(draw);
};

const stopVisualizer = () => {
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
};

// ---- finalização (MediaRecorder.onstop) ----
const finish = async () => {
  if (aborted) return;
  try {
    const mimeType = (mediaRecorder && mediaRecorder.mimeType) || 'audio/webm';
    const blob = new Blob(chunks, { type: mimeType });
    if (!blob.size) {
      emit('recordError', 'Falha ao processar o áudio gravado.');
      return;
    }

    let output = blob;
    let outputType = mimeType;
    let extension = extFor(outputType);
    if (supportsBrowserConversion(props.audioRecordFormat)) {
      try {
        output = await convertAudio(blob, props.audioRecordFormat);
        if (!output || !output.size) {
          throw new Error('empty-converted-audio');
        }
        outputType = props.audioRecordFormat;
        extension = extFor(outputType);
      } catch {
        if (requiresStrictConversion(props.audioRecordFormat)) {
          emit('recordError', 'Falha ao processar o áudio gravado.');
          return;
        }
      }
    }

    const fileName = `${getUuid()}.${extension}`;
    const file = new File([output], fileName, {
      type: outputType,
    });
    emit('finishRecord', {
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    });
  } catch (e) {
    emit('recordError', 'Falha ao processar o áudio gravado.');
  }
};

// ---- engine init ----
const initEngine = async () => {
  if (
    !navigator.mediaDevices ||
    typeof navigator.mediaDevices.getUserMedia !== 'function'
  ) {
    const err = new Error('unavailable');
    throw err;
  }
  stream = await navigator.mediaDevices.getUserMedia({ audio: true });

  audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (audioCtx.state === 'suspended') {
    try {
      await audioCtx.resume();
    } catch (e) {
      /* ignore */
    }
  }
  source = audioCtx.createMediaStreamSource(stream);
  analyser = audioCtx.createAnalyser();
  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = 0.8;
  source.connect(analyser);
  // analyser NÃO conectado ao destination → sem monitoração acoustic feedback.

  chunks = [];
  const mime = pickMime();
  mediaRecorder = mime
    ? new MediaRecorder(stream, { mimeType: mime })
    : new MediaRecorder(stream);
  mediaRecorder.ondataavailable = e => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };
  mediaRecorder.onstop = () => {
    finish();
  };
};

const start = () => {
  if (!mediaRecorder) return;
  mediaRecorder.start(250);
  isRecording = true;
  enginePaused = false;
  aborted = false;
  accPausedMs = 0;
  startedAt = performance.now();
  timeText.value = '00:00';
  emit('recorderProgressChanged', '00:00');
  startVisualizer();
  startTimer();
};

// ---- API pública (chamada pelo ReplyBox via $refs) ----
const stopRecording = () => {
  if (!isRecording) return;
  const isTooShort = recordedDurationMs() < MINIMUM_RECORDING_DURATION_MS;
  isRecording = false;
  stopVisualizer();
  stopTimer();

  // Em alguns navegadores, um toque muito rápido não produz sequer o primeiro
  // chunk do MediaRecorder. Interrompemos antes de criar uma mensagem vazia e
  // damos ao atendente um motivo acionável.
  if (isTooShort) {
    aborted = true;
  }

  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    try {
      mediaRecorder.stop(); // dispara onstop → finish() (não-abortado)
    } catch (e) {
      /* ignore */
    }
  }

  if (isTooShort) {
    emit('recordError', 'Áudio muito curto. Grave por pelo menos 2 segundos.');
  }
};

// Aceleres: pausa a gravação SEM finalizar (permite continuar/apagar/enviar)
const pauseRecording = () => {
  if (!isRecording || enginePaused) return;
  enginePaused = true;
  pauseStart = performance.now();
  stopVisualizer();
  if (mediaRecorder && mediaRecorder.state === 'recording') {
    try {
      mediaRecorder.pause();
    } catch (e) {
      /* ignore */
    }
  }
  emit('pause');
};

const resumeRecording = () => {
  if (!isRecording || !enginePaused) return;
  accPausedMs += performance.now() - pauseStart;
  enginePaused = false;
  if (mediaRecorder && mediaRecorder.state === 'paused') {
    try {
      mediaRecorder.resume();
    } catch (e) {
      /* ignore */
    }
  }
  startVisualizer();
  emit('play');
};

// Aceleres 2026-07-03: reproduz o trecho já gravado (estado pausado). Monta um
// Blob com os chunks atuais do MediaRecorder e toca num <audio>. Toggle ▶/⏸.
// NOTA: blobs webm/opus mid-recording costumam tocar no Chrome, mas não é
// garantido em todos os browsers (o container webm é finalizado só no stop).
const playback = () => {
  if (props.playbackSrc) {
    if (!playbackAudio) return;
    if (isPlayingBack.value) playbackAudio.pause();
    else
      playbackAudio.play().catch(() => {
        isPlayingBack.value = false;
      });
    return;
  }
  if (isPlayingBack.value) {
    if (playbackAudio) playbackAudio.pause();
    isPlayingBack.value = false;
    return;
  }
  if (!chunks.length) return;
  try {
    const mimeType =
      (mediaRecorder && mediaRecorder.mimeType) || 'audio/webm';
    const blob = new Blob(chunks, { type: mimeType });
    const url = URL.createObjectURL(blob);
    if (!playbackAudio) {
      playbackAudio = new Audio();
      playbackAudio.onended = () => {
        isPlayingBack.value = false;
      };
    }
    playbackAudio.src = url;
    playbackAudio
      .play()
      .then(() => {
        isPlayingBack.value = true;
      })
      .catch(() => {
        isPlayingBack.value = false;
      });
  } catch (e) {
    isPlayingBack.value = false;
  }
};

// Botão do meio: gravando → pausa. No layout de conversa, quando pausado, o
// fluxo de continuar fica exclusivamente no microfone vermelho à direita.
const onMiddleClick = () => {
  if (props.playbackSrc) {
    playback();
    return;
  }
  if (props.isPaused) {
    if (isConversationLayout.value) return;
    playback();
    return;
  }
  emit('playpause');
};

// Mic vermelho (estado pausado) → continua a gravação. Interrompe qualquer
// reprodução em curso antes de emitir o resume.
const resumeFromPause = () => {
  if (isPlayingBack.value && playbackAudio) {
    playbackAudio.pause();
    isPlayingBack.value = false;
  }
  emit('playpause');
};

// Aceleres: playback pós-stop não é usado (o strip desmonta ao anexar e o
// preview passa a ficar na bandeja de anexos). Mantido como no-op seguro.
const playPause = () => {};

// ---- cleanup ----
const cleanup = () => {
  // sinaliza que descartos (delete/send/clear) não devem emitir finishRecord
  aborted = true;
  playbackRequest?.abort();
  stopVisualizer();
  stopTimer();
  // Aceleres: interrompe reprodução do trecho gravado, se ativa.
  isPlayingBack.value = false;
  if (playbackAudio) {
    try {
      playbackAudio.pause();
    } catch (e) {
      /* ignore */
    }
    if (playbackAudio.src) {
      try {
        URL.revokeObjectURL(playbackAudio.src);
      } catch (e) {
        /* ignore */
      }
    }
    playbackAudio = null;
  }
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    try {
      mediaRecorder.stop();
    } catch (e) {
      /* ignore */
    }
  }
  if (stream) {
    stream.getTracks().forEach(t => {
      try {
        t.stop();
      } catch (e) {
        /* ignore */
      }
    });
    stream = null;
  }
  if (audioCtx) {
    try {
      audioCtx.close();
    } catch (e) {
      /* ignore */
    }
    audioCtx = null;
  }
  analyser = null;
  source = null;
  mediaRecorder = null;
  chunks = [];
};

const initPlayback = async () => {
  playbackAudio = new Audio(props.playbackSrc);
  playbackAudio.preload = 'metadata';
  const updateTime = () => {
    if (!playbackAudio) return;
    const seconds = playbackAudio.currentTime || playbackAudio.duration;
    timeText.value = formatTime(Number.isFinite(seconds) ? seconds * 1000 : 0);
  };
  playbackAudio.onloadedmetadata = updateTime;
  playbackAudio.ontimeupdate = updateTime;
  playbackAudio.onplay = () => {
    isPlayingBack.value = true;
  };
  playbackAudio.onpause = () => {
    isPlayingBack.value = false;
  };
  playbackAudio.onended = () => {
    isPlayingBack.value = false;
  };
  draw();
  // Waveform decoding is optional: playback still works if CORS or an audio
  // codec prevents decoding. It never requests microphone access.
  playbackRequest = new AbortController();
  try {
    const response = await fetch(props.playbackSrc, {
      signal: playbackRequest.signal,
    });
    if (!response.ok) return;
    const bytes = await response.arrayBuffer();
    if (aborted) return;
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const buffer = await audioCtx.decodeAudioData(bytes);
    if (aborted) return;
    const samples = buffer.getChannelData(0);
    for (let i = 0; i < BAR_COUNT; i++) {
      const start = Math.floor((i * samples.length) / BAR_COUNT);
      const end = Math.floor(((i + 1) * samples.length) / BAR_COUNT);
      let peak = 0;
      const stride = Math.max(1, Math.floor((end - start) / 100));
      for (let index = start; index < end; index += stride) {
        peak = Math.max(peak, Math.abs(samples[index]));
      }
      smooth[i] = Math.max(0.05, peak);
    }
    draw();
  } catch {
    // The media element remains the source of truth for playback.
  } finally {
    if (audioCtx) {
      audioCtx.close().catch(() => {});
      audioCtx = null;
    }
  }
};

onMounted(async () => {
  if (props.playbackSrc) {
    await initPlayback();
    return;
  }
  try {
    await initEngine();
  } catch (err) {
    cleanup();
    const name = err && err.name;
    const msg =
      name === 'NotAllowedError'
        ? 'Permissão de microfone negada. Libere o acesso no navegador para gravar áudio.'
        : name === 'NotFoundError' || name === 'OverconstrainedError'
          ? 'Nenhum microfone encontrado neste dispositivo.'
          : err && err.message === 'unavailable'
            ? 'Gravação de áudio não é suportada neste contexto (verifique permissões / se está num iframe).'
            : 'Não foi possível acessar o microfone neste contexto (verifique permissões / se está num iframe).';
    emit('recordError', msg);
    return;
  }
  try {
    start();
  } catch (e) {
    cleanup();
    emit('recordError', 'Falha ao iniciar a gravação de áudio.');
  }
});

onUnmounted(() => {
  cleanup();
});

defineExpose({
  playPause,
  stopRecording,
  pauseRecording,
  resumeRecording,
  playback,
  record: null,
});
</script>

<template>
  <!-- Aceleres 2026-07-03: barra de gravação estilo WhatsApp Web.
       Layout horizontal único ocupando 100% do reply-box:
       [Excluir] [● vermelho pulsante] [00:08] [waveform] [Pausar/Play] [Enviar]
       Pausar↔Play só troca ícone + animação (nada muda de lugar). O ponto
       vermelho some via visibility:hidden (espaço preservado → sem shift). -->
  <div
    class="acl-wa-recorder"
    :class="{
      'is-paused': isPaused,
      'acl-wa-recorder--conversation': isConversationLayout,
    }"
  >
    <!-- Aceleres: container único dos controles. A variante conversation
         reorganiza essa estrutura no CSS sem tocar na lógica do recorder. -->
    <div class="acl-wa-recorder-main">
    <button
      type="button"
      class="acl-wa-icon-btn"
      :class="{ '!hidden': readOnly }"
      :disabled="readOnly"
      aria-label="Descartar áudio"
      @click="$emit('delete')"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      </svg>
    </button>

    <span class="acl-wa-rec-dot" :class="{ '!invisible': playbackSrc }" aria-hidden="true" />

    <div class="acl-wa-audio-center">
      <span class="acl-wa-time">{{ timeText }}</span>

      <div class="acl-wa-wave">
        <canvas ref="canvasRef" class="acl-wa-canvas" />
      </div>
    </div>

    <button
      type="button"
      class="acl-wa-icon-btn acl-wa-playpause"
      :disabled="!playbackSrc && isConversationLayout && isPaused && !isPlayingBack"
      :aria-label="!playbackSrc && !isPaused ? 'Pausar gravação' : (isPlayingBack ? 'Pausar reprodução' : 'Reproduzir áudio gravado')"
      @click="onMiddleClick"
    >
      <!-- Pause: gravando OU durante a reprodução do trecho gravado -->
      <svg
        v-if="(!playbackSrc && !isPaused) || isPlayingBack"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <rect x="6" y="5" width="4" height="14" rx="1" />
        <rect x="14" y="5" width="4" height="14" rx="1" />
      </svg>
      <!-- Play: pausado e NÃO reproduzindo → inicia a reprodução -->
      <svg
        v-else
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M8 5v14l11-7z" />
      </svg>
    </button>

    <!-- Aceleres 2026-07-03: mic vermelho ao lado do Enviar. Clica pra
         CONTINUAR a gravação (resume). Tooltip customizado "Continuar gravação"
         no hover (.acl-tip-wrapper, abre pra cima).
         Sempre renderizado (sem v-if) e oculto via visibility:hidden quando NÃO
         está pausado — assim ele reserva o espaço e o waveform NÃO muda de
         largura ao pausar (mesmo padrão do .acl-wa-rec-dot). -->
    <span
      class="acl-tip-wrapper"
      :class="{ '!invisible pointer-events-none': playbackSrc && !readOnly, '!hidden': readOnly }"
      data-acl-tip="Continuar gravação"
    >
      <button
        type="button"
        class="acl-wa-mic-resume"
        :disabled="Boolean(playbackSrc)"
        aria-label="Continuar gravação"
        @click="resumeFromPause"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3z" />
          <path d="M19 11a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.92V21H9a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2h-2v-3.08A7 7 0 0 0 19 11z" />
        </svg>
      </button>
    </span>
    <button
      type="button"
      class="acl-wa-send"
      :class="{ 'invisible pointer-events-none': playbackSrc && !readOnly, '!hidden': readOnly }"
      :disabled="Boolean(playbackSrc)"
      aria-label="Enviar áudio"
      @click="$emit('send')"
    >
      <svg
        :class="isConversationLayout ? 'size-[1.88rem]' : 'size-5'"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M3 20.5l18-8.5L3 3.5 3 10l13 2-13 2z" />
      </svg>
    </button>
    </div><!-- /.acl-wa-recorder-main -->
  </div>
</template>

<style lang="scss" scoped>
/* Aceleres 2026-07-03: barra de gravação WhatsApp Web.
   Minimalista: sem bordas extras, sem fundos nos ícones, muito espaço
   em branco. Altura igual ao input normal (não aumenta o reply-box). */
.acl-wa-recorder {
  /* position:relative mantido (inofensivo) — o .acl-wa-send não é mais absoluto,
     é flex item in-flow; o relative só sobra caso algum overlay precise depois. */
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 44px;
  padding: 0 8px;
  transition: opacity 0.2s ease;
}
/* Aceleres 2026-07-03 (pedido): TODOS os controles (Excluir…mic-resume +
   Enviar) dentro deste único container, com width 70% e alinhado à direita do
   input (margin-left:auto consome os 30% de folga à esquerda). O waveform
   (flex:1) expande pra preencher os 70% — sem ficar "achatado" no canto. */
.acl-wa-recorder-main {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 70%;
  margin: 0 0 0 auto; /* alinhado à direita do input */
}

/* Botões de ícone (Excluir, Pausar/Play): sem fundo, cinza escuro, hover
   discreto. Mesmo tamanho/shape pra não variar entre estados. */
.acl-wa-icon-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
  color: #54656f;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.acl-wa-icon-btn:hover {
  background-color: rgba(0, 0, 0, 0.06);
  color: #1f2c33;
}
:deep(.dark) .acl-wa-icon-btn {
  color: #aebac1;
}
:deep(.dark) .acl-wa-icon-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
  color: #e9edef;
}

/* Mic vermelho (estado pausado) → continuar gravação. Mesmo shape dos
   ícones, mas vermelho pra destacar o "resume". Um pouco maior que os
   ícones neutros (40px) pra ter peso de ação. */
.acl-wa-mic-resume {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  color: #ff3b30;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 0.15s ease, transform 0.1s ease;
}
.acl-wa-mic-resume:hover {
  background-color: rgba(255, 59, 48, 0.12);
}
.acl-wa-mic-resume:active {
  transform: scale(0.92);
}
:deep(.dark) .acl-wa-mic-resume {
  color: #ff453a;
}

/* Aceleres 2026-07-03: botões Play/Pause e Mic um pouco maiores (40px) e
   ícones em 24px — mais clicáveis, sem eclipsar o Enviar (42px). */
.acl-wa-icon-btn.acl-wa-playpause {
  width: 40px;
  height: 40px;
}
.acl-wa-icon-btn.acl-wa-playpause svg,
.acl-wa-mic-resume svg {
  width: 24px;
  height: 24px;
}

/* Tooltip customizado "Continuar gravação" no mic vermelho. Instantâneo
   (sem o delay do title nativo), abre pra CIMA (reply-box tá no rodapé). */
.acl-tip-wrapper {
  position: relative;
  display: inline-flex;
}
.acl-tip-wrapper::after {
  content: attr(data-acl-tip);
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  background: #0f172a;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 6px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s;
  z-index: 1000;
}
.acl-tip-wrapper:hover::after {
  opacity: 1;
}

/* Ponto vermelho pulsante (~8px). Visibility:hidden quando pausado pra
   preservar o espaço (nenhum elemento se move ao trocar de estado). */
.acl-wa-rec-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff3b30;
  animation: acl-wa-pulse 1.2s ease-in-out infinite;
  visibility: visible;
}
.acl-wa-recorder.is-paused .acl-wa-rec-dot {
  visibility: hidden;
  animation: none;
}
/* Aceleres 2026-07-03: o mic-resume (.acl-tip-wrapper) é sempre renderizado,
   mas fica visibility:hidden durante a gravação (não-pausado) pra RESERVAR o
   espaço — igual ao dot acima. Assim o waveform mantém a MESMA largura antes/
   durante/depois de pausar: o botão mic só fica visível no estado pausado,
   sem empurrar/redimensionar nada. */
.acl-wa-recorder:not(.is-paused) .acl-tip-wrapper {
  visibility: hidden;
  pointer-events: none;
}
@keyframes acl-wa-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.35;
    transform: scale(0.78);
  }
}

/* Tempo: semibold, quase preto, tabular pra não "pular". */
.acl-wa-time {
  flex-shrink: 0;
  min-width: 40px;
  font-variant-numeric: tabular-nums;
  font-size: 14px;
  font-weight: 600;
  color: #0a0a0a;
  letter-spacing: 0.01em;
}
:deep(.dark) .acl-wa-time {
  color: #e9edef;
}

/* Waveform ocupa todo o espaço restante dentro do main (80%), centralizado
   vertical. Canvas com altura modesta (28px) — barras cinzas finas (#9E9E9E).
   flex:1 faz ele expandir e dar folga pros controles (não fica achatado). */
.acl-wa-wave {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: stretch;
}
.acl-wa-audio-center {
  display: contents;
}
.acl-wa-canvas {
  width: 100%;
  height: 28px;
  display: block;
}

/* Estado pausado: timer (acl-wa-time) vai pra direita e o botão play/pause
   (acl-wa-playpause) vai pra esquerda — trocam de lugar via flex order.
   Layout pausado (dentro do main 80%): [Excluir] [dot] [Play] [wave] [time]
   [mic-resume] [Send] — agora o Send também fica dentro do main (último). */
.acl-wa-recorder.is-paused .acl-wa-recorder-main > .acl-wa-icon-btn:not(.acl-wa-playpause) { order: 1; }
.acl-wa-recorder.is-paused .acl-wa-recorder-main > .acl-wa-rec-dot { order: 2; }
.acl-wa-recorder.is-paused .acl-wa-recorder-main > .acl-wa-playpause { order: 3; }
.acl-wa-recorder.is-paused .acl-wa-recorder-main > .acl-wa-wave { order: 4; }
.acl-wa-recorder.is-paused .acl-wa-recorder-main > .acl-wa-audio-center > .acl-wa-wave { order: 4; }
.acl-wa-recorder.is-paused .acl-wa-recorder-main > .acl-wa-time { order: 5; }
.acl-wa-recorder.is-paused .acl-wa-recorder-main > .acl-wa-audio-center > .acl-wa-time { order: 5; }
/* Aceleres: o botão mic-resume fica dentro de .acl-tip-wrapper (tooltip),
   então o order tem que mirar o WRAPPER (filho direto do flex), não o botão
   aninhado — senão ele cai pro order default 0 (extrema esquerda). Assim o
   mic-resume fica à esquerda do Send. */
.acl-wa-recorder.is-paused .acl-wa-recorder-main > .acl-tip-wrapper { order: 6; }
/* Send agora é filho do main — precisa de order:7 pra continuar sendo o
   último no estado pausado (senão o order default 0 o joga pra extrema esquerda). */
.acl-wa-recorder.is-paused .acl-wa-recorder-main > .acl-wa-send { order: 7; }

/* Botão Enviar: circular, verde WhatsApp, ícone branco. Maior destaque.
   Agora é flex item in-flow DENTRO do .acl-wa-recorder-main (último filho),
   sem position:absolute. O main (align-items:center) centraliza verticalmente. */
.acl-wa-send {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #00a884;
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: background-color 0.15s ease, transform 0.1s ease;
}
.acl-wa-send:hover {
  background: #069373;
}
.acl-wa-send:active {
  transform: scale(0.94);
}

/* ========== Conversation layout (Zoraxy / WhatsApp-like) ========== */
.acl-wa-recorder--conversation {
  height: 64px;
  min-height: 64px;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid rgba(229, 231, 235, 0.95);
  border-radius: 22px;
  box-shadow: none;
}

.acl-wa-recorder--conversation .acl-wa-recorder-main {
  width: min(100%, 540px);
  margin: 0 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0;
}

.acl-wa-recorder--conversation .acl-wa-audio-center {
  order: 3;
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  gap: 12px;
  width: auto;
  min-width: 0;
  margin: 0;
}

.acl-wa-recorder--conversation .acl-wa-rec-dot {
  order: 2;
  display: block;
}

.acl-wa-recorder--conversation .acl-wa-time {
  min-width: 42px;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  flex-shrink: 0;
}

.acl-wa-recorder--conversation .acl-wa-wave {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.acl-wa-recorder--conversation .acl-wa-canvas {
  height: 24px;
}

.acl-wa-recorder--conversation .acl-wa-icon-btn:not(.acl-wa-playpause) {
  order: 1;
  width: 32px;
  height: 32px;
  color: #9ca3af;
}

.acl-wa-recorder--conversation .acl-wa-icon-btn:not(.acl-wa-playpause):hover {
  background-color: transparent;
  color: #6b7280;
}

.acl-wa-recorder--conversation .acl-wa-playpause {
  order: 4;
  width: 36px;
  height: 36px;
  color: #ef4444;
  transform: none;
}

.acl-wa-recorder--conversation .acl-wa-playpause:hover {
  background-color: transparent;
  color: #dc2626;
}

.acl-wa-recorder--conversation .acl-wa-playpause svg {
  width: 18px;
  height: 18px;
}

.acl-wa-recorder--conversation .acl-tip-wrapper {
  order: 5;
  flex-shrink: 0;
}

.acl-wa-recorder--conversation .acl-wa-mic-resume {
  width: 34px;
  height: 34px;
}

.acl-wa-recorder--conversation .acl-wa-send {
  order: 6;
  width: 40px;
  height: 40px;
  background: #202c33;
  color: #ffffff;
  box-shadow: none;
  transform: none;
}

.acl-wa-recorder--conversation .acl-wa-send:hover {
  background: #000000;
  color: #ffffff;
  transform: translateY(-1px);
}

.acl-wa-recorder--conversation .acl-wa-send:active {
  transform: scale(0.94);
}

.acl-wa-recorder--conversation:not(.is-paused) .acl-tip-wrapper {
  visibility: hidden;
  pointer-events: none;
}

@media (max-width: 768px) {
  .acl-wa-recorder--conversation {
    height: 56px;
    min-height: 56px;
    padding: 6px 8px;
  }

  .acl-wa-recorder--conversation .acl-wa-recorder-main {
    width: 100%;
    gap: 7px;
  }

  .acl-wa-recorder--conversation .acl-wa-time {
    min-width: 38px;
    font-size: 13px;
  }

  .acl-wa-recorder--conversation .acl-wa-audio-center {
    gap: 6px;
  }

  .acl-wa-recorder--conversation .acl-wa-canvas {
    height: 20px;
  }

  .acl-wa-recorder--conversation .acl-wa-icon-btn:not(.acl-wa-playpause) {
    width: 28px;
    height: 28px;
  }

  .acl-wa-recorder--conversation .acl-wa-playpause,
  .acl-wa-recorder--conversation .acl-wa-mic-resume {
    width: 32px;
    height: 32px;
  }

  .acl-wa-recorder--conversation .acl-wa-send {
    width: 36px;
    height: 36px;
  }
}

/* The installation-level override stylesheet is loaded after Vite and still
   contains the retired full-width recorder grid. Keep this selector specific
   enough for the conversation recorder to retain this compact layout. */
@media (min-width: 999999px) {
  :global(div.reply-box) .acl-wa-recorder.acl-wa-recorder--conversation {
    height: 44px !important;
    min-height: 44px !important;
    padding: 0 8px !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
  }

  :global(div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-recorder-main {
    display: flex !important;
    width: min(100%, 540px) !important;
    min-height: 0 !important;
    margin: 0 0 0 auto !important;
    gap: 12px !important;
  }

  :global(div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-recorder-main
    > * {
    grid-column: auto !important;
    grid-row: auto !important;
  }

  :global(div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-recorder-main
    > .acl-wa-icon-btn:not(.acl-wa-playpause) {
    order: 1 !important;
    width: 32px !important;
    height: 32px !important;
    color: #202c33 !important;
  }

  :global(div.reply-box) .acl-wa-recorder.acl-wa-recorder--conversation .acl-wa-rec-dot {
    order: 2 !important;
    display: block !important;
  }

  :global(div.reply-box) .acl-wa-recorder.acl-wa-recorder--conversation .acl-wa-audio-center {
    order: 3 !important;
    display: flex !important;
    flex: 1 1 auto !important;
    min-width: 0 !important;
    gap: 12px !important;
  }

  :global(div.reply-box) .acl-wa-recorder.acl-wa-recorder--conversation .acl-wa-time {
    min-width: 42px !important;
    font-size: 14px !important;
  }

  :global(div.reply-box) .acl-wa-recorder.acl-wa-recorder--conversation .acl-wa-wave {
    flex: 1 1 auto !important;
    width: 100% !important;
    min-width: 0 !important;
  }

  :global(div.reply-box) .acl-wa-recorder.acl-wa-recorder--conversation .acl-wa-playpause {
    order: 4 !important;
    width: 36px !important;
    height: 36px !important;
    color: #c51f49 !important;
  }

  :global(div.reply-box) .acl-wa-recorder.acl-wa-recorder--conversation .acl-tip-wrapper {
    order: 5 !important;
  }

  :global(div.reply-box) .acl-wa-recorder.acl-wa-recorder--conversation .acl-wa-mic-resume {
    width: 34px !important;
    height: 34px !important;
  }

  :global(div.reply-box) .acl-wa-recorder.acl-wa-recorder--conversation .acl-wa-send {
    order: 6 !important;
    width: 40px !important;
    height: 40px !important;
    background: #202c33 !important;
    box-shadow: none !important;
  }
}

@media (max-width: 0px) {
  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation {
    min-height: 142px !important;
    padding: 14px 12px calc(12px + env(safe-area-inset-bottom, 0px)) !important;
    border: 0 !important;
    border-radius: 24px 24px 0 0 !important;
    background: #ffffff !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-recorder-main {
    display: flex !important;
    width: 100% !important;
    min-height: 0 !important;
    margin: 0 !important;
    gap: 7px !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-recorder-main
    > * {
    grid-column: auto !important;
    grid-row: auto !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-recorder-main
    > .acl-wa-icon-btn:not(.acl-wa-playpause) {
    order: 1 !important;
    width: 28px !important;
    height: 28px !important;
    background: transparent !important;
    color: #202c33 !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-rec-dot {
    order: 2 !important;
    display: block !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-audio-center {
    order: 3 !important;
    display: flex !important;
    flex: 1 1 auto !important;
    width: auto !important;
    min-width: 0 !important;
    gap: 6px !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-time {
    min-width: 38px !important;
    font-size: 13px !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-wave {
    flex: 1 1 auto !important;
    width: 100% !important;
    min-width: 0 !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-canvas {
    height: 20px !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-playpause {
    order: 4 !important;
    width: 32px !important;
    height: 32px !important;
    padding: 0 !important;
    background: transparent !important;
    color: #c51f49 !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation:not(.is-paused)
    .acl-wa-playpause::after {
    content: none !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-tip-wrapper {
    order: 5 !important;
    width: auto !important;
    height: auto !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-mic-resume {
    width: 32px !important;
    height: 32px !important;
    padding: 0 !important;
    background: transparent !important;
  }

  :global(.acl-whatsapp-mobile-scope div.reply-box)
    .acl-wa-recorder.acl-wa-recorder--conversation
    .acl-wa-send {
    order: 6 !important;
    width: 36px !important;
    height: 36px !important;
    background: #202c33 !important;
  }
}

@media (min-width: 769px) {
  div.reply-box.reply-box
    .acl-wa-recorder--conversation
    .acl-wa-recorder-main {
    width: 40% !important;
    margin-left: auto !important;
  }
}
</style>
